import { sumBy, uniqBy } from 'lodash-es';
import { DateTime } from 'luxon';

import {
  HydratedInvoiceRefundRow,
  InvoiceDataItem,
  InvoiceExportCondensedReportItem,
  InvoicePaymentRow,
  InvoiceRefundRow,
} from '../types';

export function useInvoiceData(
  items: InvoiceExportCondensedReportItem[],
  payments?: InvoicePaymentRow[],
  refunds?: InvoiceRefundRow[]
) {
  const updatedItems: InvoiceDataItem[] = items.map((ii) => {
    const tripLineItems = ii.items.filter(
      (item) => item.LineItemType === 'trip'
    );
    const modifierLineItems = ii.items.filter(
      (item) => item.LineItemType === 'modifier'
    );

    const priceSummaryCompatibleLineItems = ii.items.filter(
      (item) =>
        item.LineItemType === 'trip' &&
        item.TripPriceMatchesPriceCalculation === 'True' &&
        item.PriceSummary
    );

    const totalTrips = tripLineItems.length;
    const totalMiles = sumBy(ii.items, (item) => parseInt(item.Miles));
    const totalUniqPassengers = uniqBy(
      tripLineItems,
      (item) => item.PassengerID
    ).length;

    const invoiceDate = DateTime.fromFormat(ii.InvoiceDate, 'M/dd/yyyy');
    const invoiceDueDate = invoiceDate
      .plus({
        days: ii.ItemPayerInvoiceDueDays
          ? parseInt(ii.ItemPayerInvoiceDueDays)
          : 0,
      })
      .toLocaleString(DateTime.DATE_SHORT);

    const invoiceDueAmount = ii.InvoiceDueAmount
      ? parseInt(ii.InvoiceDueAmount) / 100
      : 0;

    const paymentsTotalAmount = ii.PaymentsTotalAmount
      ? parseInt(ii.PaymentsTotalAmount) / 100
      : 0;
    const tripsTotalAmount = ii.TripsTotalAmount
      ? parseInt(ii.TripsTotalAmount) / 100
      : 0;
    const modifiersTotalAmount = ii.ModifiersTotalAmount
      ? parseInt(ii.ModifiersTotalAmount) / 100
      : 0;
    const refundsTotalAmount = ii.RefundsTotalAmount
      ? parseInt(ii.RefundsTotalAmount) / 100
      : 0;

    // Could make this more efficient by making it a map lookup, but typically I
    // don't expect to have a lot of payments/refunds
    const invoicePayments =
      payments?.filter((p) => p.InvoiceID === ii.InvoiceID) ?? [];
    const invoiceRefunds =
      refunds?.filter((r) => r.InvoiceID === ii.InvoiceID) ?? [];

    const hydratedInvoiceRefunds: (HydratedInvoiceRefundRow | null)[] =
      invoiceRefunds.map((refund) => {
        const payment = invoicePayments.find(
          (p) => p.PaymentID === refund.PaymentID
        );

        // Unlikely to happen
        if (!payment) {
          return null;
        }

        return {
          ...refund,
          paymentMethod: {
            PaymentMethodID: payment.PaymentMethodID,
            IsManual: payment.IsManual,
            CardLast4: payment.CardLast4,
            CardBrand: payment.CardBrand,
            CardExpirationDate: payment.CardExpirationDate,
            CardHolderName: payment.CardHolderName,
          },
        };
      });

    return {
      tripLineItems,
      priceSummaryCompatibleLineItems,
      item: ii,
      totalTrips,
      totalMiles,
      totalUniqPassengers,
      invoiceDate,
      invoiceDueDate,
      invoiceDueAmount,
      paymentsTotalAmount,
      tripsTotalAmount,
      modifiersTotalAmount,
      refundsTotalAmount,
      payments: invoicePayments,
      refunds: hydratedInvoiceRefunds.filter(
        (n) => n !== null
      ) as HydratedInvoiceRefundRow[], // Casting to account for filtering out nulls
      modifierLineItems,
    };
  });

  return updatedItems;
}
